import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  finalize,
  forkJoin,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
  filter
} from 'rxjs';
import {
  PaginatedSearchResultMatchSearchResultDTO,
  MatchSearchResultDTO,
  PlayerRosterStatsDTO,
  Season,
  StatisticsService,
  TeamInfoDTO,
  TeamSeasonStatsDTO
} from '@solverml/api';

const EMPTY_MATCH_RESULT: PaginatedSearchResultMatchSearchResultDTO = {
  total_results: 0, page: 1, page_size: 8, data: []
};
const DEFAULT_PAGE_SIZE = 8;


@Injectable({
  providedIn: 'root'
})
export class TeamDetailStateService implements OnDestroy {

  // ---  ESTADO DE CARGA ---
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  public isLoading$ = this.isLoadingSubject.asObservable();

  private isLoadingSeasonDataSubject = new BehaviorSubject<boolean>(false);
  public isLoadingSeasonData$ = this.isLoadingSeasonDataSubject.asObservable();

  // --- ESTADO DE DATOS (Estáticos) ---
  private teamInfoSubject = new BehaviorSubject<TeamInfoDTO | null>(null);
  public teamInfo$ = this.teamInfoSubject.asObservable();

  private seasonsSubject = new BehaviorSubject<Season[] | null>(null);
  public seasons$ = this.seasonsSubject.asObservable();

  // --- ESTADO DE DATOS (Reactivos a la Temporada) ---
  private selectedSeasonStatsSubject = new BehaviorSubject<TeamSeasonStatsDTO | null>(null);
  public selectedSeasonStats$ = this.selectedSeasonStatsSubject.asObservable();

  private rosterSubject = new BehaviorSubject<PlayerRosterStatsDTO[] | null>(null);
  public roster$ = this.rosterSubject.asObservable();

  private matchesResultSubject = new BehaviorSubject<PaginatedSearchResultMatchSearchResultDTO | null>(null);
  public matchesResult$ = this.matchesResultSubject.asObservable();

  // --- INTERNO (Triggers Reactivos) ---
  private selectedSeasonIdSubject = new BehaviorSubject<number | null>(null);
  public selectedSeasonId$ = this.selectedSeasonIdSubject.asObservable();

  private currentTeamId: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(private statsApi: StatisticsService) {
    this.setupReactiveSeasonDataLoading();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public resetState(): void {
    this.isLoadingSubject.next(false);
    this.isLoadingSeasonDataSubject.next(false);
    this.teamInfoSubject.next(null);
    this.seasonsSubject.next(null);
    this.selectedSeasonStatsSubject.next(null);
    this.rosterSubject.next(null);
    this.matchesResultSubject.next(null);
    this.selectedSeasonIdSubject.next(null);
    this.currentTeamId = null;
  }

  public loadInitialData(teamId: string): void {
    if (this.isLoadingSubject.value && this.currentTeamId === teamId) {
      return;
    }

    this.isLoadingSubject.next(true);
    this.currentTeamId = teamId;

    this.resetState();
    this.currentTeamId = teamId;

    forkJoin({
      info: this.statsApi.getTeamInfoEndpoint(teamId),
      seasons: this.statsApi.getTeamSeasonsEndpoint(teamId)
    }).pipe(
      tap(({ info, seasons }) => {
        this.teamInfoSubject.next(info);
        this.seasonsSubject.next(seasons);

        if (seasons && seasons.length > 0) {
          this.selectedSeasonIdSubject.next(seasons[0].id);
        } else {
          this.isLoadingSeasonDataSubject.next(false);
        }
      }),
      catchError(err => {
        console.error(`Error loading initial data for team ${teamId}`, err);
        this.resetState();
        return of(null);
      }),
      finalize(() => {
        this.isLoadingSubject.next(false);
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  private setupReactiveSeasonDataLoading(): void {
    this.selectedSeasonIdSubject.pipe(
      filter((seasonId): seasonId is number => seasonId !== null),
      switchMap(seasonId => {
        if (!this.currentTeamId) {
          return of(null);
        }

        this.isLoadingSeasonDataSubject.next(true);
        this.selectedSeasonStatsSubject.next(null);
        this.rosterSubject.next(null);
        this.matchesResultSubject.next(null);

        return forkJoin({
          stats: this.statsApi.getTeamSeasonStatsEndpoint(this.currentTeamId, seasonId),
          roster: this.statsApi.getTeamRosterStatsEndpoint(this.currentTeamId, seasonId),
          matches: this.statsApi.searchTeamMatchesEndpoint(this.currentTeamId, seasonId, 1, DEFAULT_PAGE_SIZE)
        }).pipe(
          tap(({ stats, roster, matches }) => {
            this.selectedSeasonStatsSubject.next(stats);
            this.rosterSubject.next(roster);
            this.matchesResultSubject.next(matches);
          }),
          catchError(err => {
            console.error(`Error loading season data for team ${this.currentTeamId}, season ${seasonId}`, err);
            this.selectedSeasonStatsSubject.next(null);
            this.rosterSubject.next(null);
            this.matchesResultSubject.next(EMPTY_MATCH_RESULT);
            return of(null);
          }),
          finalize(() => {
            this.isLoadingSeasonDataSubject.next(false);
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  public changeSeason(seasonId: number): void {
    this.selectedSeasonIdSubject.next(seasonId);
  }

  public paginateMatches(page: number): void {
    const seasonId = this.selectedSeasonIdSubject.value;
    if (!this.currentTeamId || !seasonId) {
      return;
    }

    this.isLoadingSeasonDataSubject.next(true);

    this.statsApi.searchTeamMatchesEndpoint(this.currentTeamId, seasonId, page, DEFAULT_PAGE_SIZE).pipe(
      tap(matchesResult => {
        this.matchesResultSubject.next(matchesResult);
      }),
      catchError(err => {
        console.error(`Error paginating matches for team ${this.currentTeamId}`, err);
        this.matchesResultSubject.next(EMPTY_MATCH_RESULT);
        return of(null);
      }),
      finalize(() => {
        this.isLoadingSeasonDataSubject.next(false);
      }),
      takeUntil(this.destroy$)
    ).subscribe();
  }
}
